import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ProjectMeeting,
  ProjectStakeholder,
  updateProjectMeeting,
} from '../../services/commercialProjectsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { MeetingParticipantsField } from './MeetingParticipantsField';

type MeetingParticipantsDialogProps = {
  projectId: string;
  meeting: ProjectMeeting | null;
  canWrite: boolean;
  stakeholders: ProjectStakeholder[];
  onClose: () => void;
  onStakeholderCreated: (stakeholder: ProjectStakeholder) => void;
  onUpdated: (meeting: ProjectMeeting) => void;
};

export function MeetingParticipantsDialog({
  projectId,
  meeting,
  canWrite,
  stakeholders,
  onClose,
  onStakeholderCreated,
  onUpdated,
}: MeetingParticipantsDialogProps) {
  const [ids, setIds] = useState<string[]>([]);
  const requestRef = useRef(0);

  useEffect(() => {
    setIds((meeting?.participants ?? []).map((person) => person.stakeholderId));
  }, [meeting?.id]);

  async function persist(nextIds: string[]): Promise<void> {
    if (!meeting || !canWrite) return;
    const previous = ids;
    const requestId = ++requestRef.current;
    setIds(nextIds);
    try {
      const updated = await updateProjectMeeting(projectId, meeting.id, {
        title: meeting.title,
        meetingType: meeting.meetingType,
        otherTypeNote: meeting.otherTypeNote ?? '',
        scheduledAtUtc: meeting.scheduledAtUtc,
        durationMinutes: meeting.durationMinutes,
        locationOrLink: meeting.locationOrLink ?? '',
        minutes: meeting.minutes ?? '',
        decisions: meeting.decisions ?? '',
        actionItems: meeting.actionItems ?? '',
        notes: meeting.notes ?? '',
        stakeholderIds: nextIds,
      });
      if (requestId !== requestRef.current) return;
      onUpdated(updated);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setIds(previous);
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar os participantes.'));
    }
  }

  return (
    <DraggableModalPanel
      open={meeting != null}
      title="Participantes da reunião"
      subtitle={meeting?.title}
      onClose={onClose}
      className="w-full max-w-xl min-h-[min(36rem,calc(100dvh-2rem))]"
    >
      {meeting ? (
        <MeetingParticipantsField
          projectId={projectId}
          canWrite={canWrite}
          stakeholders={stakeholders}
          selectedIds={ids}
          showTitle={false}
          onChange={(nextIds) => void persist(nextIds)}
          onStakeholderCreated={onStakeholderCreated}
        />
      ) : null}
    </DraggableModalPanel>
  );
}
